import React, { useState, useRef } from 'react';
import {
	Box,
	Typography,
	TextField,
	Button,
	Paper,
	Grid2,
	Stack,
	Divider,
} from '@mui/material';
import { API_URL } from '../Config';
import { toast } from 'sonner';

const ALL_DENOMINATIONS = [
	{ label: '£ 100', value: 'hundred', factor: 100 },
	{ label: '£ 50', value: 'fifty', factor: 50 },
	{ label: '£ 20', value: 'twenty', factor: 20 },
	{ label: '£ 10', value: 'ten', factor: 10 },
	{ label: '£ 5', value: 'five', factor: 5 },
	{ label: '£ 2', value: 'two', factor: 2 },
	{ label: '£ 1', value: 'one', factor: 1 },
	{ label: '£ .50', value: 'fiftyCents', factor: 0.5 },
	{ label: '£ .20', value: 'twentyCents', factor: 0.2 },
	{ label: '£ .10', value: 'tenCents', factor: 0.1 },
	{ label: '£ .05', value: 'fiveCents', factor: 0.05 },
	{ label: '£ .02', value: 'twoCents', factor: 0.02 },
	{ label: '£ .01', value: 'oneCent', factor: 0.01 },
];

const MAX_PIECE_COUNT = 999;

const clampPieceCount = (amount) => {
	const parsed = parseInt(String(amount).replace(/[^\d]/g, ''), 10);
	if (!Number.isFinite(parsed) || parsed < 0) {
		return 0;
	}
	return Math.min(MAX_PIECE_COUNT, parsed);
};

const Opening_Balance = () => {
	const isRole19 = localStorage.getItem('user_role') === '19';
	const [focusedDenomination, setFocusedDenomination] = useState(null);
	const [denominationValues, setDenominationValues] = useState({
		hundred: 0,
		fifty: 0,
		twenty: 0,
		ten: 0,
		five: 0,
		two: 0,
		one: 0,
		fiftyCents: 0,
		twentyCents: 0,
		tenCents: 0,
		fiveCents: 0,
		twoCents: 0,
		oneCent: 0,
		other: 0
	});

	const modalRef = useRef(null);
	const accessToken = localStorage.getItem('accessToken');
	const userId = localStorage.getItem('userId');

	const handleDenominationChange = (denomination, amount) => {
		if (denomination === 'other') {
			setDenominationValues(prevState => ({
				...prevState,
				other: parseFloat(amount) || 0
			}));
			return;
		}
		const value = isRole19 ? clampPieceCount(amount) : (parseFloat(amount) || 0);
		setDenominationValues(prevState => ({
			...prevState,
			[denomination]: value
		}));
	};

	const handleInputFocus = (denomination) => {
		setFocusedDenomination(denomination);
	};

	const handleButtonClick = (buttonValue) => {
		if (focusedDenomination) {
			setDenominationValues(prevState => {
				const currentValue = prevState[focusedDenomination].toString();
				const updatedValue = currentValue + buttonValue;
				const parsed = focusedDenomination === 'other'
					? (parseFloat(updatedValue) || 0)
					: (isRole19 ? clampPieceCount(updatedValue) : (parseFloat(updatedValue) || 0));
				return {
					...prevState,
					[focusedDenomination]: parsed
				};
			});
		}
	};

	const calculateTotal = () => {
		return (
			denominationValues.hundred * 100 +
			denominationValues.fifty * 50 +
			denominationValues.twenty * 20 +
			denominationValues.ten * 10 +
			denominationValues.five * 5 +
			denominationValues.two * 2 +
			denominationValues.one * 1 +
			denominationValues.fiftyCents * 0.5 +
			denominationValues.twentyCents * 0.2 +
			denominationValues.tenCents * 0.1 +
			denominationValues.fiveCents * 0.05 +
			denominationValues.twoCents * 0.02 +
			denominationValues.oneCent * 0.01 +
			denominationValues.other
		).toFixed(2);
	};

	const submitData = async () => {
		const formData = new FormData();
		formData.append('user_id', userId);
		formData.append('hundred', denominationValues.hundred);
		formData.append('fifty', denominationValues.fifty);
		formData.append('twenty', denominationValues.twenty);
		formData.append('ten', denominationValues.ten);
		formData.append('five', denominationValues.five);
		formData.append('two', denominationValues.two);
		formData.append('one', denominationValues.one);
		formData.append('fiftyCents', denominationValues.fiftyCents);
		formData.append('twentyCents', denominationValues.twentyCents);
		formData.append('tenCents', denominationValues.tenCents);
		formData.append('fiveCents', denominationValues.fiveCents);
		formData.append('twoCents', denominationValues.twoCents);
		formData.append('oneCent', denominationValues.oneCent);
		formData.append('other', denominationValues.other);
		formData.append('total', calculateTotal());

		try {
			const response = await fetch(API_URL + '/epos-submit-opening-balance', {
				method: 'POST',
				headers: {
					'authorization': `Bearer ${accessToken}`
				},
				body: formData,
			});

			if (response.ok) {
				const result = await response.json();
				toast.success('Opening balance submitted successfully!');

				const bsModal = window.bootstrap.Modal.getInstance(modalRef.current);
				if (bsModal) {
					bsModal.hide();
				}
			} else {
				const errorData = await response.json();
				toast.error(errorData.message || 'Failed to submit opening balance');
			}
		} catch (error) {
			toast.error('Error submitting data: ' + error.message);
		}
	};

	const denominations = isRole19
		? ALL_DENOMINATIONS.filter((item) => item.value !== 'hundred')
		: ALL_DENOMINATIONS;

	const formatCurrency = (value) => {
		const symbol = localStorage.getItem("currencySymbol") || '£';
		return `${symbol}${parseFloat(value || 0).toFixed(2)}`;
	};

	const role19LeftDenominations = denominations.slice(0, 6);
	const role19RightDenominations = denominations.slice(6);

	const role19RowSx = {
		display: 'grid',
		gridTemplateColumns: '48px minmax(56px, 1fr) minmax(68px, 1fr)',
		alignItems: 'center',
		columnGap: 0.75,
	};

	const role19PieceInputSx = {
		"& .MuiOutlinedInput-root": {
			backgroundColor: "#fff",
			fontSize: '0.75rem',
			height: 30,
		},
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: '#000',
		},
		"& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: '#000',
		},
		"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: '#000',
		},
		"& .MuiInputBase-input": {
			color: '#000',
			WebkitTextFillColor: '#000',
			textAlign: 'left',
			py: 0.5,
			px: 1,
		},
	};

	const role19PoundInputSx = {
		"& .MuiOutlinedInput-root": {
			backgroundColor: "#e0e0e0",
			fontSize: '0.75rem',
			height: 30,
		},
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: '#000',
		},
		"& .MuiInputBase-input": {
			color: '#000',
			WebkitTextFillColor: '#000',
			textAlign: 'right',
			py: 0.5,
			px: 1,
		},
		"& .Mui-disabled": {
			color: '#000',
			WebkitTextFillColor: '#000',
		},
	};

	const renderRole19SectionTitle = (title) => (
		<Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#000', mb: 0.75 }}>
			{title}
		</Typography>
	);

	const renderRole19DenomRow = ({ label, value, factor }) => (
		<Box key={value} sx={role19RowSx}>
			<Typography sx={{ fontSize: '0.75rem', color: '#000', fontWeight: 500, whiteSpace: 'nowrap' }}>
				{label}
			</Typography>
			<TextField
				size="small"
				type="number"
				value={denominationValues[value]}
				onChange={(e) => handleDenominationChange(value, e.target.value)}
				onFocus={() => handleInputFocus(value)}
				inputProps={{ min: 0, max: MAX_PIECE_COUNT, step: 1 }}
				sx={role19PieceInputSx}
			/>
			<TextField
				size="small"
				type="text"
				value={(denominationValues[value] * factor).toFixed(2)}
				disabled
				sx={role19PoundInputSx}
			/>
		</Box>
	);

	const renderRole19OtherRow = () => (
		<Box sx={role19RowSx}>
			<Typography sx={{ fontSize: '0.75rem', color: '#000', fontWeight: 500 }}>
				Other
			</Typography>
			<TextField
				size="small"
				type="number"
				value={denominationValues.other}
				onChange={(e) => handleDenominationChange('other', e.target.value)}
				onFocus={() => handleInputFocus('other')}
				sx={role19PieceInputSx}
			/>
			<TextField
				size="small"
				type="text"
				value={denominationValues.other.toFixed(2)}
				disabled
				sx={role19PoundInputSx}
			/>
		</Box>
	);

	return (
		<div className="container">
			<div className="modal fade" id="opening_balance" tabIndex="-1" ref={modalRef}>
				<div
					className={`modal-dialog modal-dialog-centered ${isRole19 ? '' : 'modal-lg'}`}
					style={isRole19 ? { maxWidth: '500px', width: '100%' } : undefined}
				>
					<div className="modal-content" style={{ borderRadius: '8px', border: 'none' }}>
						<Box sx={{ p: isRole19 ? 2 : 3 }}>
							<Typography
								variant="h6"
								sx={{
									fontWeight: 600,
									fontSize: isRole19 ? "1rem" : "1.125rem",
									color: "#212b36",
									mb: isRole19 ? 1 : 2
								}}
							>
								Float In
							</Typography>

							<Typography sx={{ fontSize: isRole19 ? '0.8rem' : '0.875rem', color: '#637381', mb: isRole19 ? 1.5 : 3 }}>
								Hi, Impos Release Notes. Please process the float in.
							</Typography>

							{isRole19 ? (
								<Paper
									elevation={0}
									sx={{
										p: 1.5,
										backgroundColor: '#f5f7fa',
										borderRadius: 2,
										border: '1px solid rgba(145, 158, 171, 0.16)',
									}}
								>
									{/* <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#212b36', mb: 1.25 }}>
										Money
									</Typography> */}
									<Grid2 container spacing={1.5}>
										<Grid2 size={{ xs: 12, sm: 6 }}>
											{renderRole19SectionTitle('Pound')}
											<Stack spacing={0.75}>
												{role19LeftDenominations.map(renderRole19DenomRow)}
											</Stack>
										</Grid2>
										<Grid2 size={{ xs: 12, sm: 6 }}>
											{renderRole19SectionTitle('Pence')}
											<Stack spacing={0.75}>
												{role19RightDenominations.map(renderRole19DenomRow)}
												{renderRole19OtherRow()}
											</Stack>
										</Grid2>
									</Grid2>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											p: 1,
											mt: 1.25,
											backgroundColor: '#e3f2fd',
											borderRadius: 1,
											border: '1px solid #1976d2',
										}}
									>
										<Typography sx={{ fontSize: '0.8rem', color: '#1976d2', fontWeight: 600 }}>
											Total
										</Typography>
										<Typography sx={{ fontSize: '0.8rem', color: '#1976d2', fontWeight: 700 }}>
											{formatCurrency(calculateTotal())}
										</Typography>
									</Box>
								</Paper>
							) : (
							<Grid2 container spacing={2}>
								<Grid2 size={{ xs: 12, md: 6 }}>
									<Paper
										elevation={0}
										sx={{
											p: 2,
											backgroundColor: '#f5f7fa',
											borderRadius: 2,
											border: '1px solid rgba(145, 158, 171, 0.16)',
										}}
									>
										<Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#212b36', mb: 1.5 }}>
											Money
										</Typography>
										<Stack spacing={1.5}>
											{denominations.map(({ label, value, factor }) => (
												<Box key={value} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
													<Typography sx={{ fontSize: '0.8rem', color: '#637381', minWidth: 60 }}>
														{label}
													</Typography>
													<TextField
														size="small"
														type="number"
														value={denominationValues[value]}
														onChange={(e) => handleDenominationChange(value, e.target.value)}
														onFocus={() => handleInputFocus(value)}
														sx={{
															width: 80,
															"& .MuiOutlinedInput-root": {
																backgroundColor: "#fff",
																fontSize: '0.8rem',
															}
														}}
													/>
													<Typography sx={{ fontSize: '0.8rem', color: '#637381', mx: 0.5 }}>
														=
													</Typography>
													<TextField
														size="small"
														type="text"
														value={(denominationValues[value] * factor).toFixed(2)}
														disabled
														sx={{
															width: 100,
															"& .MuiOutlinedInput-root": {
																backgroundColor: "#e0e0e0",
																fontSize: '0.8rem',
															}
														}}
													/>
												</Box>
											))}

											<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
												<Typography sx={{ fontSize: '0.8rem', color: '#637381', minWidth: 60 }}>
													Other
												</Typography>
												<TextField
													size="small"
													type="number"
													value={denominationValues.other}
													onChange={(e) => handleDenominationChange('other', e.target.value)}
													sx={{
														width: 80,
														"& .MuiOutlinedInput-root": {
															backgroundColor: "#fff",
															fontSize: '0.8rem',
														}
													}}
												/>
												<Typography sx={{ fontSize: '0.8rem', color: '#637381', mx: 0.5 }}>
													=
												</Typography>
												<TextField
													size="small"
													type="text"
													value={denominationValues.other.toFixed(2)}
													disabled
													sx={{
														width: 100,
														"& .MuiOutlinedInput-root": {
															backgroundColor: "#e0e0e0",
															fontSize: '0.8rem',
														}
													}}
												/>
											</Box>

											<Divider sx={{ my: 1 }} />

											<Box
												sx={{
													display: 'flex',
													gap: 1,
													alignItems: 'center',
													p: 1.5,
													backgroundColor: '#e3f2fd',
													borderRadius: 1,
													border: '1px solid #1976d2',
												}}
											>
												<Typography sx={{ fontSize: '0.875rem', color: '#1976d2', fontWeight: 600, minWidth: 60 }}>
													Total
												</Typography>
												<Box sx={{ flex: 1 }} />
												<Typography sx={{ fontSize: '0.875rem', color: '#1976d2', mx: 0.5 }}>
													=
												</Typography>
												<Typography sx={{ fontSize: '0.875rem', color: '#1976d2', fontWeight: 700, minWidth: 100, textAlign: 'right' }}>
													{formatCurrency(calculateTotal())}
												</Typography>
											</Box>
										</Stack>
									</Paper>
								</Grid2>

								<Grid2 size={{ xs: 12, md: 6 }}>
									<Paper
										elevation={0}
										sx={{
											p: 2,
											backgroundColor: '#f5f7fa',
											borderRadius: 2,
											border: '1px solid rgba(145, 158, 171, 0.16)',
											height: '100%',
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
											justifyContent: 'center',
										}}
									>
										<Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#212b36', mb: 2 }}>
											Calculator
										</Typography>
										<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, maxWidth: 180 }}>
											{['7', '8', '9', '4', '5', '6', '1', '2', '3'].map((num) => (
												<Button
													key={num}
													variant="outlined"
													onClick={() => handleButtonClick(num)}
													sx={{
														minWidth: 44,
														minHeight: 44,
														fontSize: '1rem',
														fontWeight: 600,
														color: '#212b36',
														borderColor: 'rgba(145, 158, 171, 0.32)',
														'&:hover': {
															borderColor: '#1976d2',
															backgroundColor: 'rgba(25, 118, 210, 0.08)',
														}
													}}
												>
													{num}
												</Button>
											))}
										</Box>
										<Button
											variant="outlined"
											onClick={() => handleButtonClick('0')}
											sx={{
												minWidth: 180,
												minHeight: 44,
												fontSize: '1rem',
												fontWeight: 600,
												color: '#212b36',
												borderColor: 'rgba(145, 158, 171, 0.32)',
												mt: 1.5,
												'&:hover': {
													borderColor: '#1976d2',
													backgroundColor: 'rgba(25, 118, 210, 0.08)',
												}
											}}
										>
											0
										</Button>
									</Paper>
								</Grid2>
							</Grid2>
							)}

							<Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: isRole19 ? 1.5 : 3 }}>
								<Button
									data-bs-dismiss="modal"
									variant="outlined"
									className="button-variant-outlined"
									sx={{
										textTransform: "none",
										fontWeight: 500,
										color: "#212b36",
										borderColor: "#000 !important",
										px: 3,
										"&:hover": {
											borderColor: "#000 !important",
											backgroundColor: "#f5f5f5",
										},
									}}
								>
									Cancel
								</Button>
								<Button
									variant="contained"
									onClick={submitData}
								
									sx={{
										textTransform: "none",
										fontWeight: 500,
										boxShadow: "none",
										px: 3,
										backgroundColor: "#F8991D",
										color: "#fff",
										'&:hover': {
											boxShadow: "none",
											backgroundColor: "#F8991D",
											color: "#fff"
										}
									}}
								>
									Submit
								</Button>
							</Box>
						</Box>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Opening_Balance;
